import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { NgbPopoverConfig } from "@ng-bootstrap/ng-bootstrap";
import { VngDialogRef, VNG_DIALOG_DATA, VngTourService } from "@vengreso/angular-components";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { Router } from "@angular/router";
import { AuthService } from "src/app/core/core-services/services/auth.service";
import { modalStepIndex } from "src/app/core/core-services/services/onboarding-progress.service";
import { GlobalHookService } from "src/app/global-hook.service";
import { tagTourStepButtons } from "src/app/utils/tour-heap.util";
import { Subscription } from "rxjs";

const placeholders = {
  textArea:
    "Test your FlyCut here by typing it in now. Remember, if your message has any rich text elements like bold, hyper-linked words, images, etc., it will not display in simple text format.",
  testingRichEditor: "Test your FlyCut here by typing it in now.",
};

@Component({
    selector: "test-flycut",
    templateUrl: "./test-flycut.component.html",
    styleUrls: ["./test-flycut.component.scss"],
    standalone: false
})
export class TestFlycutComponent implements OnInit, OnDestroy {
  shortcut: any
  plc = placeholders;
  testElement: string = 'text';
  placeholder = true;
  testingRichEditor: string = '';
  textArea: string = placeholders.textArea;
  private dialogClosed = false;
  /** Handle for the onboarding auto-start timer, so an early close can cancel it. */
  private tourStartTimer: any = null;
  /** Guards teardownTour() so a close()-then-destroy sequence only tears down once. */
  private tourTornDown = false;
  private readonly subscriptions = new Subscription();

  constructor(
    config: NgbPopoverConfig,
    private readonly authService: AuthService,
    @Inject(VNG_DIALOG_DATA) public data: any,
    public dialogRef: VngDialogRef<TestFlycutComponent>,
    public dialog: DialogService,
    private readonly router: Router,
    private readonly tourService: VngTourService,
    private readonly globalHookService: GlobalHookService,
  ) {
    this.tourService.initialize([
      {
        anchorId: 'step8',
        content: 'Simple Text editors like LinkedIn, Slack or WhatsApp online do not allow you to embed multimedia (images, GIFs, etc) and hyperlinks.',
        title: 'Simple Text Preview',
        enableBackdrop: true,
        disablePageScrolling: true,
        placement: {
          xPosition: 'after',
          yPosition: 'above'
        },
        // Popover sits over the radio with left edges flush, so the caret hangs
        // off the bottom-left. Master's equivalent was
        // `.step8-onboarding-popover-arrow` in this component's SCSS, deleted
        // with the rest of the MatMenu-tuned offsets; see app.scss.
        popoverClass: 'fms-tour-arrow fms-tour-arrow--bottom-start'
      },
      {
        anchorId: 'step9',
        content: 'Rich Text platforms like Gmail or Outlook allow you to embed multimedia (images, GIFs, etc) and hyperlinks.',
        title: 'Rich Text Preview',
        enableBackdrop: true,
        placement: {
          xPosition: 'after',
          yPosition: 'above'
        },
        // V3-06: `after`/`above` is master's placement (popover left edge = the
        // "Rich Text Preview" radio's left edge), but this anchor starts 26px
        // further right than master's while the dialog's close "x" sits 18px
        // further left — so at vng-tour's default 320px max-width the popover ran
        // 1px past the panel and buried the "x" completely. Capping the width
        // keeps master's placement and its overhang-free clearance; see
        // `.fms-tour-step9` in src/assets/styles/app.scss.
        popoverClass: 'fms-tour-step9 fms-tour-arrow fms-tour-arrow--bottom-start'
      },
      {
        // C3-15: step10's anchor is the whole modal content box, so the
        // default `after`/`below` placement would push the popover off the
        // bottom of the dialog. The old lib compensated with a hardcoded
        // `margin-top:-400px` CSS hack (.tour-last-step-position) to slide the
        // popover up near the X close button; `before`/`above` reproduces
        // that intent (point above-and-right of the modal, near the X) using
        // vng-tour's own placement API instead of a position-coupled CSS hack.
        anchorId: 'step10',
        content: 'Click Got it! to finish testing your FlyCut.',
        title: 'Congrats!',
        enableBackdrop: true,
        placement: {
          xPosition: 'before',
          yPosition: 'above'
        },
        // Right edges flush with the modal, so a bottom-end caret lands on the
        // "x" this step is pointing at. Replaces master's
        // `.tour-last-step-position::after`, whose `bottom: 597px; left: 491px`
        // were absolute offsets for the MatMenu position this no longer uses.
        popoverClass: 'fms-tour-arrow fms-tour-arrow--bottom-end'
      }
    ]);

    this.tourService.stepShow$.subscribe((step) => {
      setTimeout(() => {
        switch (step.anchorId) {
          case 'step8':
            tagTourStepButtons('walk-me-modal-try-it-out-step-8');
            break;
          case 'step9':
            tagTourStepButtons('walk-me-modal-try-it-out-step-9');
            break;
          case 'step10':
            tagTourStepButtons('walk-me-modal-try-it-out-step-10');
            break;
        }
      }, 200);
    });

    // The last step's "Got it!" button ends the tour (VngTourService.end())
    // without closing the dialog itself — close it here so the user isn't
    // left stranded in the test modal. `close()` is idempotent (dialogClosed
    // guard), so this can't double-fire alongside the X button's own close().
    this.subscriptions.add(
      this.tourService.end$.subscribe(() => {
        this.close();
      })
    );

    if (this.isOnboarding) {
      this.tourStartTimer = setTimeout(() => {
        this.tourStartTimer = null;
        this.tourService.start();
      }, 500);
    }

    config.triggers = "hover";
    config.container = "body";
    this.shortcut = data.shortcut
  }

  get isOnboarding() {
    return this.globalHookService.onboardingActive;
  }

  ngOnInit(): void {
    this.showPlaceholder();
  }

  ngOnDestroy(): void {
    // Covers the dialog being torn down without close() ever running (e.g. a
    // hard navigation away) — the tour must not outlive this component either
    // way. teardownTour() itself guards against running twice, so this is
    // safe alongside close()'s own call to it in the ordinary X-close path.
    this.teardownTour();
    this.subscriptions.unsubscribe();
  }

  close(): void {
    if (this.dialogClosed) {
      return;
    }
    this.dialogClosed = true;
    this.teardownTour();
    this.dialogRef.close();

    if (this.globalHookService.onboardingActive) {
      // Reopen on the NEXT step (Add a FlyPlate) — issue #542.
      this.globalHookService.components.global.startModal(modalStepIndex("add_flyplate"));
    }
  }

  /**
   * Tear down whatever state of the tour this dialog is carrying, so closing
   * (or destroying) the dialog never leaves a popover/backdrop orphaned over
   * the reopened onboarding modal (#547).
   *
   * Idempotent: the real close()-then-destroy sequence calls this twice (once
   * from close(), once from the ngOnDestroy() that follows dialogRef.close()),
   * and it must only act once.
   *
   * - Always cancels the pending onboarding auto-start timer — otherwise a
   *   close during that 0-500ms window still lets the tour start later, over
   *   whatever is on screen by then.
   * - Always calls `tourService.end()`, whether or not THIS dialog's own tour
   *   ever started. `VngTourService` is a root singleton (providedIn: 'root'),
   *   and this component's own `initialize()` call above resets the service's
   *   step list/index without tearing down whatever overlay was already
   *   rendered — so a still-showing tour from a different consumer (e.g. the
   *   dashboard's own walk-me popover that opened this dialog in the first
   *   place) is silently orphaned the moment this dialog is constructed, not
   *   just if this dialog's own tour never starts. `end()` cleans up
   *   whatever overlay is currently live regardless of who rendered it, and
   *   on an already-clean service it's a verified no-op (the #540 F-1
   *   finding: no `end$` emitted, nothing to destroy). Calling it here also
   *   ends OUR OWN tour when it did start; that re-emits `end$`, which this
   *   component also listens to (see the constructor) and re-enters close() —
   *   the `dialogClosed` guard above already makes that re-entry inert.
   */
  private teardownTour(): void {
    if (this.tourTornDown) {
      return;
    }
    this.tourTornDown = true;

    if (this.tourStartTimer !== null) {
      clearTimeout(this.tourStartTimer);
      this.tourStartTimer = null;
    }

    this.tourService.end();
  }

  hidePlaceholder() {
    this.placeholder = false;
  }
  
  showPlaceholder() {
    this.placeholder = this.testingRichEditor === '' || this.testingRichEditor == placeholders.testingRichEditor;
  }

  onChangeEditor(editor: 'text' | 'edit') {
    this.testElement = editor;
    this.showPlaceholder();
    this.textArea = placeholders.textArea;
    this.testingRichEditor = placeholders.testingRichEditor;
  }
  
  checkPlaceHolder(input: string) {
    if (this[input]) {
      this[input] = null;
    } else {
      this[input] = placeholders[input];
    }
  }

  onEditorInput(event: any) {
    this.testingRichEditor = event.target.innerHTML;
  }
}
